/**
 * AS-10803 — link a SKU on a classic listing to the atlas single-SKU
 * purchase-history page.
 *
 * Warbler reaches atlas surfaces under its own `/assistant/*` prefix
 * (`/assistant/:agent/:tab*` → `AtlasEmbed`, which strips the prefix and
 * forwards the query string to the iframe). The atlas-side equivalent of this
 * helper is `PO_ROUTES.skuHistory` in `atlas/frontend/src/app/purchase-order/routes.ts`
 * — keep the two in step.
 *
 * `encodeURIComponent`, not `URLSearchParams`: the latter encodes a space as
 * `+`, which the destination would read back as a literal plus sign. SKUs are
 * free text and routinely contain `/`, spaces, `+` and `%`.
 */
const SKU_HISTORY_ROUTE = '/assistant/purchase-order/insights/sku';

/**
 * The href for a SKU's purchase history, or `null` when there is nothing to
 * look up. A blank SKU lands the merchant on an empty state, so callers render
 * plain text rather than a link that goes nowhere.
 */
export function skuHistoryPath(sku?: string | null): string | null {
  const trimmed = (sku || '').trim();
  if (!trimmed) {
    return null;
  }
  return `${SKU_HISTORY_ROUTE}?sku=${encodeURIComponent(trimmed)}`;
}

/** Screen-reader label, matching the atlas `SkuLink` wording. */
export function skuHistoryLabel(sku: string): string {
  return `Purchase order history for ${sku.trim()}`;
}

export interface SkuHistoryFrame {
  /** `isEmbedded()` from `@/utils/shopifyBridge` — true inside Shopify Admin. */
  embedded?: boolean;
  /** The store's myshopify domain, e.g. `acme.myshopify.com`. */
  shopName?: string | null;
  /** The Shopify app handle — `process.env.VUE_APP_NAME` (`assisty`). */
  appName?: string | null;
}

/**
 * The href to use for a SKU, given where the app is running.
 *
 * Inside Shopify Admin, warbler is a THIRD-PARTY iframe under
 * `admin.shopify.com`, and its Assisty JWT lives in localStorage on
 * `my.assisty.ai`. Under storage partitioning (Safari today, Chrome rolling
 * out) that write is bucketed to `admin.shopify.com`, so a first-party tab
 * opened at `my.assisty.ai` cannot see it and the merchant gets a login screen
 * instead of the page they clicked. Routing the new tab through Admin keeps it
 * first-party to `admin.shopify.com`, where App Bridge re-bootstraps the
 * session — the same construction `OrderConfirm.vue` already uses for its
 * Shopify return URL.
 *
 * Falls back to the relative `/assistant/*` path whenever the Admin URL cannot
 * be built (not embedded, or shop/app name missing). A standalone-origin link
 * still works on most browsers; a malformed Admin URL never does.
 */
export function skuHistoryHref(sku?: string | null, frame: SkuHistoryFrame = {}): string | null {
  const path = skuHistoryPath(sku);
  if (!path) {
    return null;
  }
  if (!frame.embedded) {
    return path;
  }
  const shop = toStoreHandle(frame.shopName);
  const app = (frame.appName || '').trim();
  if (!shop || !app) {
    return path;
  }
  return `https://admin.shopify.com/store/${shop}/apps/${app}${path}`;
}

const MYSHOPIFY_SUFFIX = '.myshopify.com';

/**
 * The Admin store handle for a shop name, or '' when one cannot be derived.
 *
 * Admin URLs are keyed on the handle from the **permanent** `.myshopify.com`
 * domain (`admin.shopify.com/store/<handle>/apps/<app>`). A custom storefront
 * domain is NOT a handle, so stripping a suffix that isn't there and using the
 * remainder produces a link that 404s. When the value is anything other than a
 * myshopify domain or a bare handle, return '' so the caller falls back to the
 * relative path, which always works. (CodeRabbit, MR !2820.)
 */
function toStoreHandle(shopName?: string | null): string {
  // Domains are case-insensitive and Shopify handles are lowercase, so
  // ACME.MYSHOPIFY.COM must derive the same handle as acme.myshopify.com —
  // otherwise it silently falls back to the relative path. (CodeRabbit, MR !2820.)
  const raw = (shopName || '').trim().toLowerCase();
  if (!raw) {
    return '';
  }
  const candidate = raw.endsWith(MYSHOPIFY_SUFFIX)
    ? raw.slice(0, -MYSHOPIFY_SUFFIX.length)
    // Anything else with a dot is a custom domain, not a handle.
    : raw.includes('.') ? '' : raw;
  // Validate the result: a malformed shopName like 'acme/other' would
  // otherwise inject extra path segments into the Admin URL. Shopify handles
  // are lowercase alphanumerics and hyphens. (CodeRabbit, MR !640.)
  return HANDLE_PATTERN.test(candidate) ? candidate : '';
}

const HANDLE_PATTERN = /^[a-z0-9-]+$/;
